import { ButtonGroup, Button } from "@chakra-ui/react";
import { Flex } from "@chakra-ui/layout";
import { useRef, useState } from "react";
import styled from "styled-components";
import { useEscape } from "@utils/hooks";
import { EditLink } from "../text/EditLink";
import { Label } from "../text/Label";

interface Props {
	onChange?: (str: string) => any;
	label?: string;
	hint?: string;
	className?: string;
	value?: string;
	onSave?: (str: string) => any;
	disableIfEmpty?: boolean;
	placeholder?: string;
	showPlaceholderInViewMode?: boolean;
	disableEditing?: boolean;
}

export const EditableText: React.FC<Props> = ({
	onChange,
	className,
	label,
	hint,
	value,
	onSave,
	disableIfEmpty,
	disableEditing = false,
	placeholder = "",
	showPlaceholderInViewMode = true,
}) => {
	const [editable, setEditable] = useState(false);
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const [updatedValue, setUpdatedValue] = useState(value);

	function makeEditable(e: any) {
		e?.preventDefault();
		e?.stopPropagation();

		if (disableEditing) return;

		setEditable(true);

		setTimeout(() => {
			if (textareaRef.current) {
				textareaRef.current.focus();
				textareaRef.current.select();
			}
		}, 1);
	}

	useEscape(cancel);

	function cancel(e?: any) {
		e?.preventDefault();
		e?.stopPropagation();

		setEditable(false);
		setUpdatedValue(value?.trim());
	}

	function save(e?: any) {
		e.preventDefault();
		e.stopPropagation();

		setEditable(false);
		onChange && onChange((updatedValue || "").trim());
		onSave && onSave((updatedValue || "").trim());
	}

	return (
		<Styles className={`${className ?? ""} ${disableEditing ? "disabled" : ""}`}>
			{label && (
				<Label hint={hint} className="label">
					{label}
				</Label>
			)}
			{!editable ? (
				<>
					<p onClick={(e) => makeEditable(e)}>
						{updatedValue || <span className="placeholder">{showPlaceholderInViewMode ? placeholder : ""}</span>}
					</p>
					{!disableEditing && <EditLink onClick={(e: Event) => makeEditable(e)} className="edit-link" />}
				</>
			) : (
				<div>
					<textarea
						ref={textareaRef}
						value={updatedValue}
						onChange={(e) => setUpdatedValue(e.target.value)}
						placeholder={placeholder || ""}
						onClick={(e) => e.stopPropagation()}
						style={{ border: "1px solid #ccc" }}
					/>

					<Flex justifyContent="flex-end" p="0.75rem 0 0 0" ml="0.5rem">
						<ButtonGroup>
							<Button
								colorScheme="brand"
								variant="outline"
								size="sm"
								onClick={(e) => save(e)}
								disabled={disableIfEmpty ? updatedValue === "" : false}
							>
								Spara
							</Button>
							<Button
								colorScheme="red"
								variant="outline"
								size="sm"
								onClick={(e) => cancel(e)}
								disabled={disableIfEmpty ? updatedValue === "" : false}
							>
								Avbryt
							</Button>
						</ButtonGroup>
					</Flex>
				</div>
			)}
		</Styles>
	);
};

const Styles = styled.div`
	margin: 0;
	width: 100%;

	p {
		white-space: pre-wrap;
		cursor: pointer;

		margin-top: 0.5rem;

		&:hover {
			background-color: #f9f9f9;
		}
	}
	&.disabled p {
		cursor: default;

		&:hover {
			background-color: inherit;
		}
	}

	.edit-link {
		margin-top: 0.75rem;
	}

	label {
		margin-bottom: 0.5rem;
	}

	textarea {
		display: block;
		border: none;
		overflow: auto;
		outline: none;
		resize: none;
		font-size: 1rem;
		width: 100%;
		min-height: 300px;
		line-height: ${(props) => props.theme.font.pLineHeight};
		color: ${(props) => props.theme.colors.text};
		background-color: #f9f9f9;
		padding: 0.25rem 0.5rem;
	}

	.placeholder {
		color: ${(props) => props.theme.colors.textDimmed};
	}
`;
